import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation
} from "@angular/core";
import {
    FormControl,
    ReactiveFormsModule,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    Validators
} from "@angular/forms";
import {MatPaginator, MatPaginatorModule} from "@angular/material/paginator";
import {catchError, finalize, Observable, Subject, takeUntil} from "rxjs";
import {fuseAnimations} from "@fuse/animations";
import {CommonModule, NgForOf, NgIf} from '@angular/common';
import {MatButtonModule} from '@angular/material/button';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatSelectModule} from '@angular/material/select';
import {NgxSpinnerComponent, NgxSpinnerService} from 'ngx-spinner';
import {FuseAlertComponent} from '../../../../../@fuse/components/alert';
import {FuseCardComponent} from '../../../../../@fuse/components/card';
import {RestFactoryService} from '../../../../core/services/rest-factory.service';
import {NgSelectModule} from '@ng-select/ng-select';
import {SectionTypeOptions} from '../../../../../environments/fixed-enum';
import {LayoutsManagementComponent} from "../layout-management/layouts-management.component";

@Component({
    selector: "layouts-list",
    styleUrls: ["./layouts-list.component.scss"],
    templateUrl: "./layouts-list.component.html",
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: fuseAnimations,
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatPaginatorModule,
        MatSelectModule,
        NgIf,
        ReactiveFormsModule,
        NgForOf,
        NgxSpinnerComponent,
        FuseAlertComponent,
        FuseCardComponent,
        NgSelectModule,
        LayoutsManagementComponent,

    ]
})
export class LayoutsListComponent implements OnInit, OnDestroy {

    @ViewChild(MatPaginator) private _paginator: MatPaginator;

    showList: boolean = false;
    displayAddElement: boolean = false;
    loadingMessage: string = "Loading...";
    nameSearchInputControl: UntypedFormControl = new UntypedFormControl("");
    SkinSearchInputControl: UntypedFormControl = new UntypedFormControl(0);

    title: string = "Destinations";
    reviewMode: boolean = false;

    destination_ID: string;
    destinationType: any;
    destinationTypeParent: any;
    parentControl = new FormControl(null, Validators.required);

    skinList: any[] = [];
    tableElements: any[];
    destinationParentsArray: any[];
    destinationParentsArrayObserve: Observable<any[]>;

    typingTimeout: any;
    newElementForm: UntypedFormGroup;
    alert: any;

    // API URL
    private API_GET_LAYOUTS_LIST = "/admin/skin/destinationtypelayout/list";
    private API_GET_LAYOUT = "/admin/skin/destinationtypelayout/list";
    private API_POST_NEW_DESTINATION = "/admin/destination/add";
    private API_GET_SKIN_LIST = "/admin/skin/list";

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _formBuilder: UntypedFormBuilder,
        private _changeDetectorRef: ChangeDetectorRef,
        private _restFactoryService: RestFactoryService,
        private spinner: NgxSpinnerService
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this.createForm();
        this.tableElements = [];

        // Get the data
        this.getSkinList();
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    onKeyup(): void {
        clearTimeout(this.typingTimeout);
        this.typingTimeout = setTimeout(() => {
            this.searchElements(null, true);
        }, 500);
    }

    private _filterArray(ds_name: string): any[] {
        const filterValue = ds_name.toLowerCase();
        return this.destinationParentsArray.filter(obj => obj.ds_name.toLowerCase().includes(filterValue));
    }

    /**
     * Create the form
     */
    createForm(): void {
        this.newElementForm = this._formBuilder.group({
            ds_name: [null, Validators.required],
        });
        // this.newElementForm.get('id_type').disable();
    }

    getSkinList() {
        const payload = new FormData();
        this._restFactoryService.httpPostService(this.API_GET_SKIN_LIST, payload)
            .pipe(
                takeUntil(this._unsubscribeAll),
                finalize(() => {
                    this._changeDetectorRef.detectChanges();
                }),
                catchError((err) => {
                    return err;
                })
            )
            .subscribe(
                (response) => {
                    this.skinList = response?.ar_skins;
                    this.skinList.unshift({ds_airline: "All", id_airline: 0});
                    if (this.skinList.length > 1) {
                        this.SkinSearchInputControl.setValue(this.skinList[1].id_airline);
                    }
                    this.searchElements(null);
                    this._changeDetectorRef.markForCheck();
                });
    }

    /**
     * Open new-search-window dialog
     */
    searchElements(searcher: string, skip: boolean = false): void {
        const dsName = this.nameSearchInputControl.getRawValue();
        const idAirline = this.SkinSearchInputControl.getRawValue();
        const payload = new FormData();
        payload.append("ds_name", dsName);
        if (idAirline !== null && idAirline !== 0) {
            payload.append("id_airline", idAirline);
        }
        let dynaAPI = this.API_GET_LAYOUTS_LIST;
        this.tableElements = [];
        this.loadingMessage = null;
        this.spinner.show().then();

        this._unsubscribeAll.next(null);
        this._restFactoryService.httpPostService(dynaAPI, payload)
            .pipe(
                takeUntil(this._unsubscribeAll),
                finalize(() => {
                    this._changeDetectorRef.detectChanges();
                }),
                catchError((err) => {
                    this.spinner.hide().then();
                    this.loadingMessage = "No results found!";
                    return err;
                })
            )
            .subscribe(
                (response) => {
                    this.spinner.hide().then(
                        () => {
                            this.tableElements = response.ar_results;

                            // this._paginator.length = response.vi_TotalRows;
                            if (this.tableElements.length === 0) {
                                this.loadingMessage = "No results found";
                            }
                            this._changeDetectorRef.markForCheck();
                        }
                    );
                });
    }

    /**
     * Handle command from the child component
     */
    handleCommand(command: string) {
        // Do something with the command
        this.destination_ID = null;
        this.showList = !this.showList;
        this._changeDetectorRef.markForCheck();
        if (command) {
            this.onKeyup();
        }
    }

    /**
     * Switch to the child component
     */
    switchToDestination(dest: string): void {
        this.destination_ID = dest;
        this.displayAddElement = false;
        this.showList = !this.showList;
        this._changeDetectorRef.detectChanges();
    }

    removeHtmlTags(str: string): string {
        return str.replace(/<[^>]*>/g, '');
    }

    createDestination(event: Event): void {
        event.preventDefault();
        const payload = new FormData();

        payload.append("ds_name", this.newElementForm.get('ds_name').value);
        payload.append("id_destinationtype", this.newElementForm.get('id_type').value);
        payload.append("id_destinationparent", this.newElementForm.get('id_parent').value);

        this._unsubscribeAll.next(null);
        this._restFactoryService.httpPostService(this.API_POST_NEW_DESTINATION, payload)
            .pipe(
                takeUntil(this._unsubscribeAll),
                finalize(() => {
                    this._changeDetectorRef.detectChanges();
                }),
                catchError((err) => {
                    return err;
                })
            )
            .subscribe(
                (response) => {
                    this.nameSearchInputControl.setValue(this.newElementForm.get('ds_name').value);
                    this.switchToDestination(response.id_destination);
                });
    }

    protected readonly componentTypeList = SectionTypeOptions;
}
